import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { env } from "../../env";
import { Loading } from "../../Loading";
import { IntegrationsClient, SendGridClient, useSendGridSession } from "../../sendgrid-auth";
import { useAccountRegionCookie } from "../../sendgrid-auth/useAccountRegionCookie";
import { AccountRegion } from "../../types/regionalApi";
import { useAccountQueries, useUnifiedAuth } from "../../unified-auth";
import { UserTypeResult } from "../../unified-auth/context";

export const logoutPathQueryFunction = async (
  makoToken: string | undefined,
  accountRegion: AccountRegion | undefined
) => {
  try {
    // If a mako token or accountRegion is not defined then always just return '/';
    if (typeof makoToken === "undefined" || !accountRegion) {
      return "/";
    }

    const integrationsClient = new IntegrationsClient({
      baseURL: env.VITE_LINKS_URL || "https://links.integrations.sendgrid.net",
    });
    const logoutConfigResult = await integrationsClient.fetchLogoutConfig();

    if (!logoutConfigResult.success) {
      return "/";
    }

    const client = new SendGridClient({
      baseURL: env.VITE_API_BASE_URL || "https://api.sendgrid.com",
    });
    const camAccountResult = await client.fetchCAMAccount(makoToken, accountRegion);

    if (!camAccountResult.success) {
      return "/";
    }

    const catalogOrg = camAccountResult.catalog_org;
    return (
      (catalogOrg &&
        (logoutConfigResult.config && logoutConfigResult.config[catalogOrg])?.sso_logout) ||
      `/`
    );
  } catch (error) {
    // If something goes wrong just return '/'
    return "/";
  }
};

export const Logout = () => {
  const { logout } = useUnifiedAuth();
  const { getUserType } = useAccountQueries();
  const { makoToken, isAuthenticated } = useSendGridSession();
  const didLogout = useRef(false);
  const { accountRegion, setAccountRegionCookie } = useAccountRegionCookie();

  const { data: userType, isError: isGetUserTypeError } = useQuery<UserTypeResult>({
    enabled: isAuthenticated && !Boolean(accountRegion),
    queryKey: ["getUserType"],
    queryFn: async () => {
      const result = await getUserType(AccountRegion.Global);

      setAccountRegionCookie(result.region);
      return result;
    },
    staleTime: 0,
  });

  const { data: path } = useQuery({
    queryKey: ["logoutPath", makoToken],
    enabled: isAuthenticated && Boolean(accountRegion),
    queryFn: () => logoutPathQueryFunction(makoToken, accountRegion),
    staleTime: 0,
  });

  const checkIfLoggedOut = () => {
    if (!didLogout.current) {
      didLogout.current = true;
      return false;
    }

    return didLogout.current;
  };

  useEffect(() => {
    (async () => {
      if (path) {
        if (checkIfLoggedOut()) {
          return;
        }
        await logout({ returnTo: path, region: accountRegion ?? userType?.region });
        return;
      }

      if (isGetUserTypeError) {
        if (checkIfLoggedOut()) {
          return;
        }
        await logout({ returnTo: "/", region: accountRegion ?? userType?.region });
      }
    })();
  }, [logout, path, didLogout, accountRegion, isGetUserTypeError, userType]);

  return <Loading />;
};
